"use client";

import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import {
  format,
  isBefore,
  startOfToday,
  startOfMonth,
  endOfMonth,
  isAfter,
  parse,
  isSameMonth,
  addDays,
  startOfDay,
  addMonths,
  subMonths,
  getDaysInMonth,
} from "date-fns";
import { useCallback, useEffect, useRef, useState } from "react";
import { SelectedSlot, TimeSlot } from "@/types/slots";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { ApiEndpoint, apiService } from "../../lib/api";
import { Availability } from "../../types/availability";
import { browserTimezone, getFirstAvailableDate } from "../../helper/utility";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { SlotBtn } from "./slot-btn";
import { TimezoneAndDuration } from "../components/timezone-duration";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { formatInTimeZone } from "date-fns-tz";
import { addMinutes } from "date-fns";
import { EventDuration } from "../../types/event-duration";
import { usePixelTracker } from "../../hooks/pixel-tracker";
import env from "../../lib/env";

interface EventSlotsProps {
  resource: any;
  durations: EventDuration[];
  onSlotSelect: (slot: SelectedSlot) => void;
  userId?: number | undefined;
  layout?: "default" | "column";
}

export function EventSlots({
  resource,
  durations,
  onSlotSelect,
  userId,
  layout = "default",
}: EventSlotsProps) {
  const today = startOfDay(new Date());
  const isLoggedIn =
    typeof window !== "undefined" ? window.DS_PAGE?.login : false;
  const TEST_ACCOUNTS =
    typeof window !== "undefined"
      ? [1386, 1].includes(window.DS_PAGE?.page?.account_id ?? -1)
      : false;

  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [startDate, setStartDate] = useState(
    resource.period?.type === "ranges"
      ? new Date(resource.period.start_date)
      : today
  );
  const [use24Hour, setUse24Hour] = useState(false);
  const [availability, setAvailability] = useState<Availability | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [duration, setDuration] = useState<number>(
    durations.find((d) => d.is_default)!.minutes
  );
  const [timeZone, setTimeZone] = useState<string>(browserTimezone);
  const slotsRef = useRef<HTMLDivElement | null>(null);
  const hasScrolledToSlots = useRef(false);
  const tracker = usePixelTracker();

  const isCapacityPublic =
    (resource.event_type === "group" ||
      resource.event_type === "round_robin") &&
    resource.capacity?.is_public;

  const getAvailability = useCallback(
    async (date: Date, duration: number, timeZone: string) => {
      setIsLoading(true);
      setError(null);

      try {
        const startDate = isSameMonth(today, date)
          ? format(today, "yyyy-MM-dd")
          : format(startOfMonth(date), "yyyy-MM-dd");
        const endDate = format(endOfMonth(date), "yyyy-MM-dd");

        const url =
          !env.IS_PROD || TEST_ACCOUNTS
            ? `${ApiEndpoint.slots}/${resource.resource_id}`
            : `${ApiEndpoint.availability}/${resource.resource_id}`;

        const response = await apiService.list(url, {
          start: startDate,
          end: endDate,
          duration: duration,
          time_zone: timeZone,
          user_id: userId,
        });
        setAvailability(response);
      } catch (err: any) {
        setError(err.message || "Failed to load time slots");
        setAvailability(null);
      } finally {
        setIsLoading(false);
      }
    },
    [resource.resource_id]
  );

  useEffect(() => {
    getAvailability(startDate, duration, timeZone);
  }, [startDate, duration, timeZone, getAvailability]);

  useEffect(() => {
    let isAutoSelected = false;
    if (availability) {
      if (
        !selectedDate ||
        !availability.slots.some(
          (x) => x.date === format(selectedDate, "yyyy-MM-dd")
        )
      ) {
        const firstAvailable = getFirstAvailableDate(availability);
        if (firstAvailable) {
          const d = parse(firstAvailable, "yyyy-MM-dd", new Date());
          setSelectedDate(d);
          isAutoSelected = true;
        }
      }
      setTimeZone(availability.time_zone);
    }
    // Dummy date set to stop loading
    if (!isAutoSelected && !selectedDate) {
      setSelectedDate(startDate);
    }
  }, [availability, selectedDate]);

  const getTimeSlots = (date: Date) => {
    if (!availability?.slots) return [];
    const dateStr = format(date, "yyyy-MM-dd");
    const slot = availability.slots.find((s) => s.date === dateStr);
    return slot?.hours || [];
  };

  const isDisabled = (date: Date) => {
    if (isBefore(date, startOfToday())) {
      return true;
    }
    const dateStr = format(date, "yyyy-MM-dd");
    return !availability?.slots.some(
      (x) => x.date === dateStr && x.hours.some((y) => y.available > 0)
    );
  };

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);

    // Scroll only on mobile and only once
    if (
      window.innerWidth <= 768 &&
      !hasScrolledToSlots.current &&
      slotsRef.current
    ) {
      slotsRef.current.scrollIntoView({ behavior: "smooth" });
      hasScrolledToSlots.current = true;
    }

    // Meta, GA4 tracking
    tracker("date_selected", {
      label: date?.toISOString().split("T")[0],
    });
  };

  const handleTimeSelect = (
    dateTime: string,
    duration: number,
    time_zone: string
  ) => {
    const selectedSlot: SelectedSlot = {
      start_at: dateTime,
      end_at: formatInTimeZone(
        addMinutes(new Date(dateTime), duration),
        time_zone,
        "yyyy-MM-dd'T'HH:mm:ssxxx"
      ),
      duration: duration,
      time_zone: time_zone,
    };
    onSlotSelect(selectedSlot);

    // Meta, GA4 tracking
    tracker("time_selected", {
      label: dateTime,
    });
  };

  const handlePrevious = () => {
    setSelectedDate(undefined);
    setStartDate(startOfMonth(subMonths(startDate, 1)));
  };

  const handleNext = () => {
    setSelectedDate(undefined);
    setStartDate(startOfMonth(addMonths(startDate, 1)));
  };

  const handleToday = () => {
    setStartDate(startOfMonth(today));
  };

  const ErrorMessage = ({ message }: { message: string }) => (
    <Alert
      variant="destructive"
      className="flex flex-col items-center text-center flex flex-col gap-2 p-4"
    >
      <AlertDescription>{message}</AlertDescription>
      <Button variant="outline" onClick={() => handleNext()}>
        Next month
      </Button>
    </Alert>
  );

  const SlotsComponent = () => {
    if (isLoading || !selectedDate) {
      return (
        <div className="inset-0 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary my-32"></div>
        </div>
      );
    }

    const slots = getTimeSlots(selectedDate);
    if (slots.length === 0) {
      return (
        <ErrorMessage
          message={`No slots available on ${format(startDate, "MMM, yyyy")}`}
        />
      );
    }

    return slots
      .filter((s) => isLoggedIn || s.available)
      .map((slot: TimeSlot) => (
        <SlotBtn
          key={slot.time}
          slot={slot}
          duration={duration}
          timeZone={timeZone}
          use24Hour={use24Hour}
          timeFormat={resource.settings?.time_format}
          isCapacityPublic={isCapacityPublic}
          onTimeSelect={handleTimeSelect}
        />
      ));
  };

  if (layout === "column") {
    const currentMonth = startOfMonth(startDate);
    const daysInMonth = getDaysInMonth(currentMonth);
    const monthDays = Array.from({ length: daysInMonth }, (_, i) => {
      const day = addDays(currentMonth, i);
      // Only include dates that are today or in the future
      return isBefore(day, today) ? null : day;
    }).filter(Boolean) as Date[]; // Remove null values (past dates)

    return (
      <div className="flex flex-col h-full overflow-hidden">
        {/* Toolbar */}
        <div className="border-b p-4 flex items-center justify-between bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-20">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handlePrevious}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={handleNext}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={handleToday}>
              Today
            </Button>
            <span className="text-sm font-medium px-2">
              {format(startDate, "MMM d")} -{" "}
              {format(addMonths(startDate, 1), "MMM d, yyyy")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md border border-input overflow-hidden">
              <Button
                variant={!use24Hour ? "secondary" : "ghost"}
                size="sm"
                className="h-8 rounded-none border-0"
                onClick={() => setUse24Hour(false)}
              >
                12h
              </Button>
              <div className="w-px bg-border h-8" />
              <Button
                variant={use24Hour ? "secondary" : "ghost"}
                size="sm"
                className="h-8 rounded-none border-0"
                onClick={() => setUse24Hour(true)}
              >
                24h
              </Button>
            </div>

            <TimezoneAndDuration
              resource={resource}
              durations={durations}
              duration={duration}
              onDurationChange={(d) => setDuration(d)}
              onTimezoneChange={(tz) => setTimeZone(tz)}
            />
          </div>
        </div>

        {/* Scrollable Slots Area */}
        <div className="h-full overflow-hidden">
          <ScrollArea className="h-full w-full relative">
            {isLoading && (
              <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            )}
            <div className="min-w-max">
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-background">
                  <TableRow>
                    {monthDays.map((date) => (
                      <TableHead
                        key={date.toISOString()}
                        className="min-w-[200px] text-center"
                      >
                        <div className="font-medium">{format(date, "EEE")}</div>
                        <div className="text-sm text-muted-foreground">
                          {format(date, "MMM d")}
                        </div>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {/* Get maximum number of slots across all days */}
                  {(() => {
                    const maxSlots = Math.max(
                      ...monthDays.map((date) => getTimeSlots(date).length)
                    );
                    return Array.from({ length: maxSlots }).map(
                      (_, slotIndex) => (
                        <TableRow
                          key={slotIndex}
                          className="hover:bg-transparent hover:text-inherit"
                        >
                          {monthDays.map((date) => {
                            const daySlots = getTimeSlots(date);
                            const slot = daySlots[slotIndex];
                            return (
                              <TableCell
                                key={date.toISOString()}
                                className="py-2"
                              >
                                {slot ? (
                                  <SlotBtn
                                    key={slot.time}
                                    slot={slot}
                                    duration={duration}
                                    timeZone={timeZone}
                                    use24Hour={use24Hour}
                                    timeFormat={resource.settings?.time_format}
                                    isCapacityPublic={isCapacityPublic}
                                    onTimeSelect={handleTimeSelect}
                                  />
                                ) : slotIndex === 0 && daySlots.length === 0 ? (
                                  <p className="text-sm text-muted-foreground py-2">
                                    No slots available
                                  </p>
                                ) : null}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      )
                    );
                  })()}
                </TableBody>
              </Table>
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] xl:grid-cols-[1fr_300px] gap-4 h-full">
      <div className="p-6 md:border-r">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <h3 className="text-lg font-semibold">Select a Date & Time</h3>
          <TimezoneAndDuration
            resource={resource}
            durations={durations}
            duration={duration}
            onDurationChange={(d) => setDuration(d)}
            onTimezoneChange={(tz) => setTimeZone(tz)}
          />
        </div>
        <div className="flex-1 w-full">
          <div className="relative">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
              disabled={isDisabled}
              className="h-full w-full flex"
              classNames={{
                months: "flex w-full flex-col sm:flex-row gap-4 flex-1",
                month: "gap-4 w-full flex flex-col",
                table: "w-full h-full border-collapse gap-1",
                head_row: "",
                row: "w-full",
                cell: "relative p-0 pr-1 pb-1 sm:p-2.5 text-center focus-within:relative focus-within:z-20",
                day: "h-9 w-9 md:h-12 md:w-12 p-0 font-normal aria-selected:opacity-100 rounded-md inline-flex items-center justify-center text-sm",
                day_selected:
                  "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
                day_today: "bg-accent text-accent-foreground",
              }}
              month={startDate}
              onMonthChange={setStartDate}
              modifiers={{
                outsideCurrentMonth: (date) =>
                  isBefore(date, startOfMonth(startDate)) ||
                  isAfter(date, endOfMonth(startDate)),
                notDisabled: (date) => !isDisabled(date),
              }}
              modifiersClassNames={{
                notDisabled: "bg-accent",
                outsideCurrentMonth: "hidden:inline-flex",
              }}
              modifiersStyles={{
                outsideCurrentMonth: {
                  display: "none",
                },
              }}
            />
            {isLoading && (
              <div className="absolute inset-0 bg-background/50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div>
        <div className="flex justify-between items-center p-6 gap-3">
          <h3 className="text-lg font-semibold">
            {selectedDate
              ? format(selectedDate, "MMM d, yyyy")
              : "Select a date"}
          </h3>

          <div className="flex justify-end items-center gap-2">
            {/* Time Format Dropdown */}
            <div className="inline-flex rounded-md border border-input overflow-hidden">
              <Button
                variant={!use24Hour ? "secondary" : "ghost"}
                size="sm"
                className="h-8 rounded-none border-0"
                onClick={() => setUse24Hour(false)}
              >
                12h
              </Button>
              <div className="w-px bg-border h-8" />
              <Button
                variant={use24Hour ? "secondary" : "ghost"}
                size="sm"
                className="h-8 rounded-none border-0"
                onClick={() => setUse24Hour(true)}
              >
                24h
              </Button>
            </div>
          </div>
        </div>

        <ScrollArea type="always" className="h-full lg:h-[425px]">
          <div
            id="slots"
            ref={slotsRef}
            className="p-6 pt-0 flex flex-col gap-2"
          >
            {error ? <ErrorMessage message={error} /> : <SlotsComponent />}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
